import React, { useContext, useState } from "react";
import { useMutation } from "react-query";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import * as api from "../services";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import AppContext from "../appContext";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";
import useErrorMessages from "../Home/useErrorMessages";

const EMAIL_ID = "email";
const PASSWORD_ID = "password";
const TWO_FACTOR_ID = "twoFactor";

export default function ConnectionLost({ open, handleClose, code }) {
  const { t } = useTranslation();
  const {
    app: { sessionId },
    dispatch,
  } = useContext(AppContext);
  const {
    accountSettings: { email: initEmail, lang },
  } = useAccountSettings();

  const { headerCodes } = useErrorMessages();
  const getErrorMessages = () => {
    switch (code) {
      // offline
      case 0:
        return {
          title: t(`errors.offline.title`),
          text: t(`errors.offline.text`)
        }
      // timeout
      case -1:
        return {
          title: t(`errors.timeout.title`),
          text: t(`errors.timeout.text`),
        };
      // maintenance
      case 500:
      case 501:
      case 503:
      case 501:
        return {
          title: t(`errors.maintenance.title`),
          text: t(`errors.maintenance.text`),
        };
      // unknown error
      default:
        return {
          title: headerCodes?.[code]?.[lang] ?? t(`errors.general.title`),
          text: t(`errors.general.text`),
        };
    }
  };

  // general form error
  const [isGeneralError, setIsGeneralError] = useState(false);
  const [generalHelperText, setGeneralHelperText] = useState("");
  // email
  const [email, setEmail] = useState(initEmail);
  const [emailHelperText, setEmailHelperText] = useState("");
  const [isEmailError, setIsEmailError] = useState(false);
  const handleEmail = (event) => setEmail(event.target.value);
  // password
  const [password, setPassword] = useState("");
  const [passwordHelperText, setPasswordHelperText] = useState("");
  const [isPasswordError, setIsPasswordError] = useState("");
  const handlePassword = (event) => setPassword(event.target.value);
  // twoFactor
  const [isTwoFactor, setIsTwoFactor] = useState(false);
  const [twoFactor, setTwoFactor] = useState("");
  const [twoFactorHelperText, setTwoFactorHelperText] = useState("");
  const [isTwoFactorError, setIsTwoFactorError] = useState(false);
  const handleTwoFactor = (event) => setTwoFactor(event.target.value);

  const handleEnter = () => {
    if (initEmail === "") window.location = window.REACT_APP_LOGOUT_ADDRESS;
    // redirect to login if email of user isn't defined
  };

  const loginMutation = useMutation(
    () => {
      let loginPayload = { email: email, password: password, sessionId };
      if (isTwoFactor && twoFactor !== "") loginPayload["2FACode"] = twoFactor;
      return api.postLogin(loginPayload);
    },
    {
      onError: (data) => {
        setIsGeneralError(true);
        setGeneralHelperText(t("common.errors.errorDialog.title"));
      },
      onSuccess: (data) => {
        const {
          data: { status, sessionId: newSessionId },
        } = data;
        if (status === 1) {
          handleClose(); // succesful login
          dispatch({ type: "SET_SESSION_ID", payload: newSessionId });
          dispatch({ type: "PAUSE_QUERIES", payload: false });
        }
        if (status === 2) {
          // legal needs updating
          setIsGeneralError(true);
          setGeneralHelperText(t("account.errors.updateLegal"));
        }
        if (status === 3) {
          // email not verified
          setIsEmailError(true);
          setEmailHelperText(t("account.errors.email2Verified"));
        }
        if (status === 0 || status === 4) {
          // Wrong pass
          setIsPasswordError(true);
          setPasswordHelperText(t("account.errors.wrongPass"));
        }
        const twoFactorFailed = () => {
          setIsPasswordError(true);
          setPasswordHelperText(t("account.errors.login"));
          setIsTwoFactor(false);
          setTwoFactor("");
        };
        if (status === 5) {
          // two factor
          if (isTwoFactor) {
            twoFactorFailed();
          } else {
            setIsTwoFactor(true);
          }
        }
        if (status === 6) {
          // two factor failed
          twoFactorFailed();
        }
        if (status === 7) {
          // login barred
          setIsPasswordError(true);
          setPasswordHelperText(t("account.errors.barred"));
        }
        // other error
        //setIsGeneralError(true);
        //setGeneralHelperText(t("common.errors.errorDialog.title"));
      },
    }
  );

  const handleLogin = () => {
    const clearErrors = () => {
      setEmailHelperText("");
      setIsEmailError(false);
      setPasswordHelperText("");
      setIsPasswordError(false);
      setTwoFactorHelperText("");
      setIsTwoFactorError(false);
      setIsGeneralError(false);
      setGeneralHelperText("");
    };
    // clear validation
    clearErrors();
    const isError = () => {
      let error;
      // check if values are not empty
      if (email === "") {
        setIsEmailError(true);
        setEmailHelperText(t("account.errors.emptyEmail"));
        error = true;
      }
      if (password === "") {
        setIsPasswordError(true);
        setPasswordHelperText(t("account.errors.emptyPass"));
        error = true;
      }
      if (isTwoFactor && twoFactor === "") {
        setIsTwoFactorError(true);
        setTwoFactorHelperText(t("account.errors.emptyTwoFactor"));
        error = true;
      }
      return error;
    };
    if (!isError()) {
      loginMutation.mutate();
    }
  };

  const textFieldDefaults = {
    fullWidth: true,
    margin: "dense",
    onKeyDown: (e) => {
      if (e.code?.includes("Enter")) {
        handleLogin();
      }
    },
  };

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth={"sm"}
        aria-labelledby="connection-lost-dialog-title"
        disableEscapeKeyDown={true}
        TransitionProps={{
          onEnter: handleEnter,
        }}
        sx={{ maxWidth: "sm", margin: "auto" }}
      >
        <DialogTitle id="conntection-lost-dialog-title">
          {getErrorMessages().title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{getErrorMessages().text}</DialogContentText>
          <form noValidate autoComplete="off">
            {/** email */}
            <TextField
              {...textFieldDefaults}
              id={EMAIL_ID}
              label={t("account.profile.email")}
              type="email"
              disabled={initEmail !== ""}
              autoFocus={initEmail === ""}
              error={isEmailError || isGeneralError}
              helperText={emailHelperText}
              value={email}
              onChange={handleEmail}
              autoComplete="email"
            />
            {/** Password */}
            <TextField
              {...textFieldDefaults}
              id={PASSWORD_ID}
              label={t("account.profile.password")}
              type="password"
              autoFocus={initEmail !== ""}
              autoComplete="current-password"
              error={isPasswordError || isGeneralError}
              helperText={passwordHelperText}
              value={password}
              onChange={handlePassword}
            />
            {/** TwoFactor */}
            {isTwoFactor ? (
              <TextField
                {...textFieldDefaults}
                id={TWO_FACTOR_ID}
                label={t("account.profile.twoFactor")}
                type="text"
                autoFocus={isTwoFactor}
                error={isTwoFactorError || isGeneralError}
                helperText={twoFactorHelperText}
                value={twoFactor}
                onChange={handleTwoFactor}
              />
            ) : null}
          </form>
          {isGeneralError ? (
            <Alert severity="error">{generalHelperText}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogin} color="primary">
            {t("navigation.login")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
